import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  BookOpenIcon,
  MapPinIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import { Step } from "../PrintModal";
import { PrintModalStepper } from "./PrintModalStepper";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  steps: Step[];
  resetSteps: () => void;
};

type ConfirmPrintingParams = {
  confirm_printing: boolean;
};

export const PrintModalStep2: FC<Props> = ({
  open,
  setOpen,
  resetSteps,
  steps,
}) => {
  const { t } = useTranslation();
  const { book, refresh, print_orders, subscription } = useContext(BookContext);
  const print_order = print_orders?.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];

  // Form Methods =============================================================
  const ConfirmPrintingSchema = Yup.object().shape({
    confirm_printing: Yup.boolean()
      .required(t("errors.required"))
      .oneOf([true], t("errors.required")),
  });

  const defaultValues = useMemo<ConfirmPrintingParams>(() => {
    return {
      confirm_printing: false,
    };
  }, [book]);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ConfirmPrintingParams>({
    defaultValues: defaultValues,
    resolver: yupResolver(ConfirmPrintingSchema),
  });

  // Mutations ===============================================================
  const ConfirmPrintingMutation = useCreateMutation("/confirm_printing");

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: ConfirmPrintingParams) => {
      ConfirmPrintingMutation.mutateAsync({
        confirm_printing: values.confirm_printing,
      }).then(() => {
        toast.success(t("printModal.confirmation_success"));
        refresh();
        resetSteps();
        setOpen(false);
      });
    },
    [refresh, t]
  );

  // After every hook (rules of hooks)
  if (!book) {
    return null;
  }

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between "
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                  >
                    {t("printModal.order_confirmation")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />
                  <PrintModalStepper steps={steps} />
                  <div
                    className="mt-4 text-sm sm:text-base text-slate-700 font-semibold text-left"
                    dangerouslySetInnerHTML={{
                      __html: t("printModal.order_confirm_description", {
                        interpolation: {
                          escapeValue: false,
                        },
                      }),
                    }}
                  />
                  <div className="flex flex-col space-y-4 mt-4">
                    <div className="flex flex-col w-full sm:w-2/3 sm:mx-auto rounded space-y-4">
                      <div className="flex flex-row rounded-md bg-delicate-50 items-center p-2">
                        <div className="bg-delicate-200 rounded-md m-2 p-2">
                          <BookOpenIcon className="w-5 h-5 sm:w-6 sm:h-6 text-delicate-800" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm sm:text-base">
                            {t("printModal.number_of_book")} :{" "}
                            {subscription?.books_remaining}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-row rounded-md bg-delicate-50 items-center p-2">
                        <div className="bg-delicate-200 rounded-md m-2 p-2">
                          <MapPinIcon className="w-5 h-5 sm:w-6 sm:h-6 text-delicate-800" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm sm:text-base">
                            {t("printModal.delivery_address")} :
                          </p>
                          <p className="text-sm sm:text-base">
                            {print_order?.first_name} {print_order?.last_name}
                            {"  "}
                            {print_order?.phone}
                          </p>
                          <p className="text-sm sm:text-base">
                            {print_order?.address}
                          </p>
                          <p className="text-sm sm:text-base">
                            {print_order?.address_line_2}
                          </p>
                          <p className="text-sm sm:text-base">
                            {print_order?.zip_code} {print_order?.city},{" "}
                            {print_order?.country}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex">
                      <input
                        type="checkbox"
                        id="confirm_printing"
                        {...register("confirm_printing")}
                        className="mr-2"
                      />
                      <label
                        htmlFor="confirm_printing"
                        className="block text-sm sm:text-base font-medium text-slate-900"
                      >
                        {t("printModal.confirm_order")}
                      </label>
                    </div>
                    <div className="flex">
                      {errors.confirm_printing && (
                        <span className="text-rust-400 font-nunito text-sm">
                          {errors.confirm_printing.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("printModal.confirm")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
